/**
 * Subscription & billing — current plan, animated pricing cards with a
 * monthly/yearly toggle, usage meters, billing history and payment methods.
 *
 * A premium SaaS-style billing surface (Stripe/Linear-inspired).
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import { SettingsCard, Pill } from '@/src/modules/components/settings/primitives';
import { ConfirmModal } from '@/src/modules/components/settings/ConfirmModal';
import { useToast } from '@/src/modules/components/settings/ToastProvider';
import { getInvitations } from '@/src/infrastructure/lib/api';

type PlanId = 'free' | 'pro' | 'premium';

const PLANS: {
  id: PlanId;
  name: string;
  tagline: string;
  monthly: number;
  features: string[];
  recommended?: boolean;
}[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For your first celebration.',
    monthly: 0,
    features: ['1 invitation website', 'Limited gallery uploads', 'Basic themes'],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For hosts who do it often.',
    monthly: 12,
    recommended: true,
    features: [
      'Unlimited invitations',
      'Premium themes',
      'RSVP analytics',
      'Custom domain support',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'For planners and teams.',
    monthly: 29,
    features: [
      'AI-generated customisation',
      'Advanced analytics',
      'Team collaboration',
      'Priority support',
      'Premium animations',
    ],
  },
];

/** Maps the backend plan tier (FREE/BASIC/PRO/STUDIO) to a display plan. */
function toPlanId(tier?: string): PlanId {
  if (tier === 'STUDIO') return 'premium';
  if (tier === 'PRO' || tier === 'BASIC') return 'pro';
  return 'free';
}

export function SubscriptionSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const currentPlan = toPlanId(user?.plan);

  const [yearly, setYearly] = useState(true);
  const [target, setTarget] = useState<PlanId | null>(null);

  const [invitationsCount, setInvitationsCount] = useState<number>(0);
  const [galleryCount, setGalleryCount] = useState<number>(0);
  const [rsvpCount, setRsvpCount] = useState<number>(0);

  useEffect(() => {
    let active = true;
    getInvitations()
      .then((invites) => {
        if (!active || !Array.isArray(invites)) return;
        setInvitationsCount(invites.length);
        const gallery = invites.reduce((acc, inv) => acc + (inv.mediaUrls?.length || 0), 0);
        const rsvps = invites.reduce((acc, inv) => acc + (inv.totalRsvps || 0), 0);
        setGalleryCount(gallery);
        setRsvpCount(rsvps);
      })
      .catch(() => {
        // Fallback for missing/unauthenticated responses
      });
    return () => {
      active = false;
    };
  }, []);

  const usageItems = [
    {
      label: 'Invitation websites',
      used: invitationsCount,
      limit: currentPlan === 'free' ? 1 : 10,
      icon: Icons.Templates,
    },
    {
      label: 'Gallery uploads',
      used: galleryCount,
      limit: currentPlan === 'free' ? 25 : 250,
      icon: Icons.Camera,
    },
    {
      label: 'RSVP responses',
      used: rsvpCount,
      limit: currentPlan === 'free' ? 100 : 1000,
      icon: Icons.Users,
    },
  ];

  const paymentMethod = (user as any)?.paymentMethod as
    | { brand?: string; last4?: string; expMonth?: number; expYear?: number }
    | undefined;

  const invoices = ((user as any)?.invoices || []) as Array<{
    id: string;
    date: string;
    amount: string;
    status: string;
  }>;

  function priceFor(monthly: number) {
    if (monthly === 0) return { amount: 0, suffix: '' };
    return yearly
      ? { amount: Math.round(monthly * 12 * 0.8), suffix: '/year' }
      : { amount: monthly, suffix: '/month' };
  }

  const targetPlan = PLANS.find((p) => p.id === target);

  return (
    <div className="flex flex-col gap-6">
      {/* Current plan */}
      <SettingsCard className="overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icons.Crown size={22} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-on-surface capitalize">
                  {currentPlan} plan
                </h3>
                <Pill tone="primary">Current</Pill>
              </div>
              <p className="text-sm text-on-surface-variant">
                {currentPlan === 'free'
                  ? 'No billing — upgrade any time.'
                  : (user as any)?.subscriptionRenewsAt
                  ? `Renews on ${(user as any).subscriptionRenewsAt} · auto-renew on`
                  : 'Active subscription · auto-renew on'}
              </p>
            </div>
          </div>
          {currentPlan !== 'premium' && (
            <Button onClick={() => setTarget(currentPlan === 'free' ? 'pro' : 'premium')}>
              <Icons.Zap size={16} className="mr-1.5" />
              Upgrade plan
            </Button>
          )}
        </div>
      </SettingsCard>

      {/* Usage */}
      <SettingsCard
        icon={Icons.Analytics}
        title="Current usage"
        description="Your activity for this billing period."
      >
        <div className="grid sm:grid-cols-3 gap-5">
          {usageItems.map((u) => {
            const pct = Math.min(100, Math.round((u.used / u.limit) * 100));
            const near = pct >= 80;
            return (
              <div key={u.label} className="rounded-2xl bg-surface-low p-4">
                <div className="flex items-center gap-2 text-on-surface-variant">
                  <u.icon size={15} />
                  <span className="text-xs font-semibold">{u.label}</span>
                </div>
                <p className="mt-2 text-xl font-bold text-on-surface">
                  {u.used}
                  <span className="text-sm text-on-surface-variant font-medium">
                    {' '}
                    / {u.limit}
                  </span>
                </p>
                <div className="mt-2 h-1.5 rounded-full bg-outline-variant/30 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className={cn(
                      'h-full rounded-full',
                      near ? 'bg-status-processing' : 'bg-primary',
                    )}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      {/* Pricing cards */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-on-surface">Plans &amp; pricing</h3>
            <p className="text-sm text-on-surface-variant">
              Switch or cancel at any time.
            </p>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-surface-low p-1">
            {(['monthly', 'yearly'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setYearly(mode === 'yearly')}
                className={cn(
                  'relative rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors',
                  (mode === 'yearly') === yearly
                    ? 'text-white'
                    : 'text-on-surface-variant',
                )}
              >
                {(mode === 'yearly') === yearly && (
                  <motion.span
                    layoutId="billing-pill"
                    className="absolute inset-0 rounded-full bg-primary"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative">{mode}</span>
                {mode === 'yearly' && (
                  <span className="relative ml-1 text-[10px] font-bold text-status-published">
                    -20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const price = priceFor(plan.monthly);
            const isCurrent = plan.id === currentPlan;
            return (
              <motion.div
                key={plan.id}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                className={cn(
                  'relative flex flex-col rounded-[1.75rem] border p-6 transition-shadow',
                  plan.recommended
                    ? 'border-primary/40 bg-surface-lowest shadow-lg shadow-primary/5'
                    : 'border-outline-variant/30 bg-surface-lowest shadow-sm hover:shadow-md',
                )}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Pill tone="primary" className="shadow-sm">
                      <Icons.Sparkles size={11} /> Recommended
                    </Pill>
                  </div>
                )}
                <h4 className="text-lg font-bold text-on-surface">{plan.name}</h4>
                <p className="text-xs text-on-surface-variant mt-0.5">{plan.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="text-3xl font-extrabold text-on-surface">
                    ${price.amount}
                  </span>
                  <span className="text-sm text-on-surface-variant mb-1">
                    {price.suffix}
                  </span>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-on-surface">
                      <Icons.Check
                        size={16}
                        className="mt-0.5 shrink-0 text-status-published"
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className="mt-6 w-full justify-center"
                  variant={
                    isCurrent ? 'secondary' : plan.recommended ? 'primary' : 'outline'
                  }
                  disabled={isCurrent}
                  onClick={() => setTarget(plan.id)}
                >
                  {isCurrent
                    ? 'Current plan'
                    : plan.monthly === 0
                      ? 'Downgrade'
                      : `Choose ${plan.name}`}
                </Button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Payment method */}
      <SettingsCard
        icon={Icons.Subscription}
        title="Payment method"
        description="Used for subscription renewals."
        action={
          paymentMethod ? (
            <Button variant="outline" size="sm" onClick={() => toast('Payment editor coming soon.', 'info')}>
              Update
            </Button>
          ) : undefined
        }
      >
        {paymentMethod ? (
          <div className="flex items-center gap-4 rounded-2xl bg-surface-low p-4">
            <div className="w-12 h-8 rounded-md bg-gradient-to-br from-primary to-brand-deep flex items-center justify-center text-white text-[10px] font-bold">
              {paymentMethod.brand || 'CARD'}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-on-surface">•••• •••• •••• {paymentMethod.last4}</p>
              <p className="text-xs text-on-surface-variant">
                Expires {paymentMethod.expMonth} / {paymentMethod.expYear}
              </p>
            </div>
            <Pill tone="success">
              <Icons.Check size={11} /> Default
            </Pill>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-surface-low p-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-outline-variant/10 text-on-surface-variant flex items-center justify-center">
                <Icons.Subscription size={18} />
              </span>
              <div>
                <p className="text-sm font-semibold text-on-surface">No payment method on file</p>
                <p className="text-xs text-on-surface-variant">Add a payment method to easily upgrade or renew plans.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => toast('Payment gateway integration in progress.', 'info')}>
              <Icons.Plus size={14} className="mr-1.5" />
              Add payment method
            </Button>
          </div>
        )}
      </SettingsCard>

      {/* Billing history */}
      <SettingsCard
        icon={Icons.History}
        title="Billing history"
        description="Download invoices for your records."
      >
        {invoices.length === 0 ? (
          <div className="text-center py-6">
            <Icons.History size={28} className="mx-auto text-on-surface-variant/40 mb-2" />
            <p className="text-sm font-semibold text-on-surface">No billing history yet</p>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Invoices and payment receipts will appear here once you make a purchase.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-outline-variant/15">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-on-surface">{inv.id}</p>
                  <p className="text-xs text-on-surface-variant">{inv.date}</p>
                </div>
                <span className="text-sm font-semibold text-on-surface">{inv.amount}</span>
                <Pill tone="success">{inv.status}</Pill>
                <button
                  type="button"
                  onClick={() => toast(`Downloading ${inv.id}…`, 'info')}
                  className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-low hover:text-primary transition-colors"
                  title="Download invoice"
                >
                  <Icons.Download size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      <ConfirmModal
        open={target !== null}
        onClose={() => setTarget(null)}
        tone="primary"
        title={`Switch to the ${targetPlan?.name ?? ''} plan?`}
        description={
          targetPlan
            ? `You'll be billed $${priceFor(targetPlan.monthly).amount}${
                priceFor(targetPlan.monthly).suffix
              }. Changes take effect immediately.`
            : ''
        }
        confirmLabel="Confirm change"
        onConfirm={async () => {
          await new Promise((r) => setTimeout(r, 900));
          toast(`You're now on the ${targetPlan?.name} plan.`);
        }}
      />
    </div>
  );
}

